/**
 * Client-side signup / login analytics for the dashboard.
 *
 * Signups complete via <SignUpButton mode="modal">, whose `forceRedirectUrl`
 * carries `welcome=1` (`/?welcome=1` on the home hero, welcomeRedirectUrl()
 * on pages that must keep their own URL). Clerk routes there ONLY after a
 * genuine sign-up, so the `welcome` marker — not the old fragile `createdAt < 2min`
 * heuristic — is the signal that a new account was just created.
 *
 * Note: the Google Ads *conversion* is owned server-side by the Clerk
 * `user.created` webhook. These functions fire GA4/Vercel analytics only.
 */

import { trackSignup, trackLogin } from '@privisy/ui-bridge';

const WELCOME_PARAM = 'welcome';
const WELCOME_VALUE = '1';

/** True if the welcome marker is present in the search string or hash query. */
export function hasWelcomeMarker(): boolean {
  if (typeof window === 'undefined') return false;
  const inSearch = new URLSearchParams(window.location.search).get(WELCOME_PARAM) === WELCOME_VALUE;
  const hashQuery = window.location.hash.split('?')[1] ?? '';
  const inHash = new URLSearchParams(hashQuery).get(WELCOME_PARAM) === WELCOME_VALUE;
  return inSearch || inHash;
}

/**
 * The current URL with the welcome marker added to its search string, for a
 * <SignUpButton forceRedirectUrl> on a page the visitor should land back on
 * (the claim landing, a scan route). Claim params are kept, and so is the
 * hash route unless `hash` replaces it. The search string changes, so Clerk's
 * redirect is a full page load rather than a same-document hash navigation.
 */
export function welcomeRedirectUrl(hash: string = window.location.hash): string {
  const { pathname, search } = window.location;
  const params = new URLSearchParams(search);
  params.set(WELCOME_PARAM, WELCOME_VALUE);
  return `${pathname}?${params.toString()}${hash}`;
}

/** Remove the welcome marker from both the search string and any hash query. */
export function stripWelcomeMarker(): void {
  if (typeof window === 'undefined' || !window.history?.replaceState) return;

  const cleanQuery = (raw: string): string => {
    const qs = raw.startsWith('?') ? raw.slice(1) : raw;
    const params = new URLSearchParams(qs);
    params.delete(WELCOME_PARAM);
    const s = params.toString();
    return s ? `?${s}` : '';
  };

  const { pathname, search, hash } = window.location;
  let newHash = hash;
  const qIndex = hash.indexOf('?');
  if (qIndex !== -1) {
    newHash = hash.slice(0, qIndex) + cleanQuery(hash.slice(qIndex));
  }
  const newUrl = pathname + cleanQuery(search) + newHash;
  window.history.replaceState(null, '', newUrl);
}

interface SignupOpts {
  userId: string;
  email?: string;
  /** Called after the signup event fires (e.g. to hit the LinkedIn CAPI). */
  onFired?: (userId: string, email?: string) => void;
}

/**
 * Fire the GA4 `sign_up` event exactly once when the welcome marker is
 * present, then strip the marker. Guarded by sessionStorage keyed on userId
 * so a reload with a stale marker does not re-fire. Returns whether it fired.
 */
export async function maybeFireSignupConversion({ userId, email, onFired }: SignupOpts): Promise<boolean> {
  if (!hasWelcomeMarker()) return false;

  const guardKey = `signup_conversion_fired_${userId}`;
  try {
    if (window.sessionStorage.getItem(guardKey)) {
      stripWelcomeMarker();
      return false;
    }
    window.sessionStorage.setItem(guardKey, '1');
  } catch {
    // sessionStorage blocked — stripping the marker below still prevents a
    // re-fire within this page load.
  }

  await trackSignup(userId, email);
  onFired?.(userId, email);
  stripWelcomeMarker();
  return true;
}

/**
 * Fire the GA4 `login` event once per session for a returning user. Skips
 * when this visit is a fresh sign-up (welcome marker present or the signup
 * guard already set this session), so an acquisition is never miscounted as
 * a login. Returns whether it fired.
 */
export function maybeFireLogin(userId: string): boolean {
  const signupGuard = `signup_conversion_fired_${userId}`;
  const loginGuard = `signin_conversion_fired_${userId}`;
  try {
    if (hasWelcomeMarker()) return false;
    if (window.sessionStorage.getItem(signupGuard)) return false;
    if (window.sessionStorage.getItem(loginGuard)) return false;
    window.sessionStorage.setItem(loginGuard, '1');
  } catch {
    return false;
  }
  trackLogin(userId);
  return true;
}
